import { themeBoxShadow, themeColor } from '@config/theme';
import { ChatTabType } from '@features/chat/types/types';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import Icon from '@mdi/react';
import { Box, Tab, Tabs } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useChatStore } from '../store';
import { tabNavigationTabs } from '../tabs';

const styles = {
	container: {
		position: 'relative',
		width: '100%',
		height: '72px',
	},
	tabs: {
		boxShadow: themeBoxShadow.main,
		backgroundColor: themeColor.veryLightBeige,
		width: '100%',
		position: 'absolute' as const,
		top: 0,
		left: 0,
		zIndex: 10,
	},
	tab: (isActive: boolean) => ({
		color: isActive ? themeColor.teal : themeColor.gray,
		backgroundColor: isActive ? themeColor.lightGreen : '',
		borderRadius: '30px',
		fontSize: '12px',
		fontWeight: isActive ? 'bold' : 'normal',
		textTransform: 'none',
		cursor: 'pointer',
		'&:first-of-type': { borderRadius: '0px 30px 30px 0px' },
		'&:last-child': { borderRadius: '30px 0px 0px 30px' },
		'&:not(:first-of-type):not(:last-child)': { borderRadius: '30px' },
	}),
};

export const TabNavigation: React.FC = () => {
	const { activeTab, setActiveTab } = useChatStore();
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { navigateWithLanguage } = useLanguageNavigate();

	const handleTabChange = (event: React.SyntheticEvent, newValue: ChatTabType) => {
		setActiveTab(newValue);
		const newParams = new URLSearchParams(searchParams);
		newParams.set('tab', newValue);
		setSearchParams(newParams);
	};

	return (
		<Box sx={styles.container}>
			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				variant="fullWidth"
				TabIndicatorProps={{ style: { display: 'none' } }}
				sx={styles.tabs}
			>
				{tabNavigationTabs.map((tab) => (
					<Tab
						key={tab.label}
						label={t(tab.translationKey)}
						icon={<Icon path={tab.icon} size={1} />}
						value={tab.label as ChatTabType}
						sx={styles.tab(activeTab === tab.label)}
					/>
				))}
			</Tabs>
		</Box>
	);
};
